import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useCanvas } from '../../../hooks/useCanvas';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';
import { getGlobalCreateAiSprite } from '../../../stores/globalStore';
import TimelineControls from './TimelineControls';
import TimelineTrack from './TimelineTrack';
import CaptionSubTrack from './CaptionSubTrack';
import Playhead from './Playhead';
import { autoScrollTimeline, autoScrollTimelineVertical } from '../../../utils/timelineUtils';
import { Volume, VolumeX } from 'lucide-react';
import TimelineContextMenu from '../../ContextMenu/TimelineContextMenu';
import ContextMenu from '../../ContextMenu/ContextMenu';

const Timeline = ({ createVideoSprite, createImageSprite, createAudioSprite, createGifSprite, onReplaceAction, addComponentToCanvas }) => {
  const {
    currentTime,
    duration,
    canvasElements,
    selectedElements,
    selectedElement,
    moveSelectedElements,
    selectElement,
    selectElements,
    toggleElementSelection,
    selectElementsInRange,
    setCurrentTime,
    toggleMute,
    stopPlaybackLoop,
    reorderElement,
    deleteElement,
    undo,
    redo,
    canUndo,
    canRedo,
    copyElements,
    getClipboard,
    pasteElements,
    updateElement,
  } = useCanvas();

  // Refs for synchronized scrolling
  const scrollContainerRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const layerPropertiesRef = useRef(null);
  const contextMenuRef = useRef(null);

  //states
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [currentZoom, setCurrentZoom] = useState(duration);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [firstSelectedElement, setFirstSelectedElement] = useState(null);
  const [lastSelectedElement, setLastSelectedElement] = useState(null);
  const [globalDragState, setGlobalDragState] = useState(null);
  const [snapEnabled, setSnapEnabled] = useState(true);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    renderX: 0,
    renderY: 0,
    targetElement: null,
  });

  // Hide context menu when clicking elsewhere
  const hideContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);


  const handleSnapToggle = useCallback(() => {
    setSnapEnabled(prev => !prev);
  }, []);

  const handleSpriteMove = useCallback(
    (event, shortcutName, shortcutConfig) => {
      if (selectedElement && shortcutConfig.payload) {
        const { x, y } = shortcutConfig.payload;
        moveSelectedElements(x, y);
      }
    },
    [selectedElement, moveSelectedElements]
  );

  // Clear snap locks when snapping is toggled off during an active drag
  useEffect(() => {
    if (!snapEnabled && globalDragState?.snapLock) {
      setGlobalDragState(prev => {
        if (prev?.snapLock) {
          const { snapLock, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    }
  }, [snapEnabled, globalDragState?.snapLock]);

  // Handle right-click context menu
  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      if (!timelineContainerRef.current) return;
      const rect = timelineContainerRef.current.getBoundingClientRect();
      const rawX = event.clientX - rect.left;
      const rawY = event.clientY - rect.top;
      setContextMenu({
        visible: true,
        x: rawX,
        y: rawY,
        renderX: rawX,
        renderY: rawY,
        targetElement: lastSelectedElement || (selectedElements.length > 0 ? selectedElements[0] : null),
      });
    },
    [selectedElements, lastSelectedElement]
  );

  // Close context menu on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (contextMenu.visible && !event.target.closest('.context-menu')) {
        hideContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [contextMenu.visible, hideContextMenu]);

  useEffect(() => {
    if (!contextMenu.visible || !timelineContainerRef.current || !contextMenuRef.current) return;

    const containerRect = timelineContainerRef.current.getBoundingClientRect();
    const menuRect = contextMenuRef.current.getBoundingClientRect();
    const edgePadding = 8;

    const maxLeft = Math.max(edgePadding, containerRect.width - menuRect.width - edgePadding);
    const nextLeft = Math.max(edgePadding, Math.min(contextMenu.x, maxLeft));

    let nextTop = contextMenu.y - menuRect.height;
    if (nextTop < edgePadding) {
      nextTop = contextMenu.y;
    }

    const maxTop = Math.max(edgePadding, containerRect.height - menuRect.height - edgePadding);
    nextTop = Math.max(edgePadding, Math.min(nextTop, maxTop));

    const leftChanged = Math.abs(nextLeft - contextMenu.renderX) > 0.5;
    const topChanged = Math.abs(nextTop - contextMenu.renderY) > 0.5;

    if (leftChanged || topChanged) {
      setContextMenu((prev) => (
        prev.visible
          ? { ...prev, renderX: nextLeft, renderY: nextTop }
          : prev
      ));
    }
  }, [contextMenu.visible, contextMenu.x, contextMenu.y, contextMenu.renderX, contextMenu.renderY]);

  const sortedVisualElements = [...canvasElements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));

  // Timeline configuration
  const LAYER_NAME_WIDTH = 156;
  const TRACK_HEIGHT = 56;

  // Dynamic tick interval based on duration
  const getTickInterval = () => {
    if (duration >= 120) return 15;
    if (duration >= 60) return 10;
    if (duration >= 30) return 5;
    if (duration >= 10) return 0.5;
    return 0.2;
  }
  const TICK_INTERVAL = getTickInterval();

  // Calculate timeline width based on zoom
  const timelineAreaWidth = timelineRef.current?.clientWidth || 800;
  const zoomRatio = duration > 0 ? currentZoom / duration : 1;
  const timelineWidth = Math.max(timelineAreaWidth, timelineAreaWidth * zoomRatio);

  // Adjust scroll position on zoom to keep playhead in view
  useEffect(() => {
    if (scrollContainerRef.current && timelineRef.current && !globalDragState) {
      const playheadPosition = (currentTime / duration) * timelineWidth;
      const containerWidth = timelineRef.current.clientWidth;
      const currentScrollLeft = scrollContainerRef.current.scrollLeft;

      if (playheadPosition < currentScrollLeft) {
        scrollContainerRef.current.scrollLeft = playheadPosition;
        setScrollLeft(playheadPosition);
      } else if (playheadPosition > currentScrollLeft + containerWidth) {
        const newScrollLeft = playheadPosition - containerWidth + 50;
        scrollContainerRef.current.scrollLeft = newScrollLeft;
        setScrollLeft(newScrollLeft);
      }
    }
  }, [currentZoom]);

  // Focus management helper
  const focusTimeline = useCallback(() => {
    if (timelineContainerRef?.current) {
      timelineContainerRef.current.focus();
    }
  }, []);

  // Helper functions for multi-select support
  const isMultiSelectPressed = useCallback((e) => {
    return e.ctrlKey || e.metaKey;
  }, []);

  const isRangeSelectPressed = useCallback((e) => {
    return e.shiftKey;
  }, []);

  // Handle scroll event from the main container (timeline tracks)
  const handleScroll = (e) => {
    const newScrollLeft = e.target.scrollLeft;
    const newScrollTop = e.target.scrollTop;

    setScrollLeft(newScrollLeft);
    setScrollTop(newScrollTop);

    if (layerPropertiesRef.current && layerPropertiesRef.current.scrollTop !== newScrollTop) {
      layerPropertiesRef.current.scrollTop = newScrollTop;
    }
  };

  // Handle scroll from layer properties
  const handleLayerPropertiesScroll = (e) => {
    const newScrollTop = e.target.scrollTop;
    setScrollTop(newScrollTop);

    if (scrollContainerRef.current && scrollContainerRef.current.scrollTop !== newScrollTop) {
      scrollContainerRef.current.scrollTop = newScrollTop;
    }
  };

  // Utility function to calculate percentage position for any time value
  const getTimePercentage = (time) => {
    return duration > 0 ? (time / duration) * 100 : 0;
  };

  const updatePlayheadPosition = useCallback((e) => {
    if (!timelineRef.current) return;
    stopPlaybackLoop();
    const rulerRect = timelineRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rulerRect.left;
    const absoluteX = relativeX + scrollLeft;
    const newTime = (absoluteX / timelineWidth) * duration;
    const clampedTime = Math.max(0, Math.min(duration, newTime));
    setCurrentTime(clampedTime);

    if (isDraggingPlayhead && scrollContainerRef.current) {
      const { scrolled, newScrollLeft } = autoScrollTimeline(e, scrollContainerRef.current);
      if (scrolled) {
        setScrollLeft(newScrollLeft);
      }
    }
  }, [stopPlaybackLoop, scrollLeft, timelineWidth, duration, isDraggingPlayhead, setCurrentTime]);

  const handlePlayheadMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPlayhead(true);
    updatePlayheadPosition(e);
  }, [updatePlayheadPosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDraggingPlayhead) updatePlayheadPosition(e);
  }, [isDraggingPlayhead, updatePlayheadPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDraggingPlayhead(false);
  }, []);

  const handleRulerClick = useCallback((e) => {
    if (isDraggingPlayhead || e.target.closest('[data-playhead-handle]')) return;
    updatePlayheadPosition(e);
  }, [isDraggingPlayhead, updatePlayheadPosition]);

  const handleElementClick = useCallback((element, e) => {
    focusTimeline();

    if (isMultiSelectPressed(e)) {
      e.preventDefault();
      e.stopPropagation();
      toggleElementSelection(element);
      setLastSelectedElement(element);
    } else if (isRangeSelectPressed(e) && firstSelectedElement) {
      e.preventDefault();
      e.stopPropagation();
      selectElementsInRange(firstSelectedElement, element);
      setLastSelectedElement(element);
    } else {
      selectElement(element);
      setFirstSelectedElement(element);
      setLastSelectedElement(element);
    }
  }, [focusTimeline, isMultiSelectPressed, isRangeSelectPressed, firstSelectedElement, toggleElementSelection, selectElementsInRange, selectElement]);

  const handleTimelineClick = useCallback(
    (e) => {
      const interactiveElement = e.target.closest('.cursor-pointer, .drag-handle, svg, [data-interactive] .context-menu');
      if (!interactiveElement) {
        selectElement(null);
        setFirstSelectedElement(null);
        setLastSelectedElement(null);
        focusTimeline();
        hideContextMenu();
      }
    },
    [selectElement, focusTimeline, hideContextMenu]
  );

  const shortcutCallbacks = {
    SELECT_ALL: useCallback(() => {
      selectElements([...canvasElements]);
    }, [selectElements, canvasElements]),

    DELETE: useCallback(() => {
      if (selectedElements.length > 0) {
        console.log('🗑️ Deleting selected elements from timeline');
        selectedElements.forEach(el => {
          deleteElement(el.id);
        });
        selectElements([]);
      }
    }, [selectedElements, deleteElement, selectElements]),

    DELETE_ALT: useCallback(() => {
      if (selectedElements.length > 0) {
        console.log('🗑️ Deleting selected elements from timeline (Alt)');
        selectedElements.forEach(el => {
          deleteElement(el.id);
        });
        selectElements([]);
      }
    }, [selectedElements, deleteElement, selectElements]),

    UNDO: useCallback(() => {
      if (canUndo()) {
        console.log('⏪ Undoing last action from timeline');
        undo({
          createVideoSprite,
          createImageSprite,
          createAudioSprite,
          createGifSprite,
          createAiSprite: getGlobalCreateAiSprite(),
        });
      }
    }, [canUndo, undo, createVideoSprite, createImageSprite, createAudioSprite, createGifSprite]),

    REDO: useCallback(() => {
      if (canRedo()) {
        console.log('⏩ Redoing action from timeline');
        redo({
          createVideoSprite,
          createImageSprite,
          createAudioSprite,
          createGifSprite,
          createAiSprite: getGlobalCreateAiSprite(),
        });
      }
    }, [canRedo, redo, createVideoSprite, createImageSprite, createAudioSprite, createGifSprite]),

    REDO_ALT: useCallback(() => {
      if (canRedo()) {
        console.log('⏩ Redoing action from timeline (Alt)');
        redo({
          createVideoSprite,
          createImageSprite,
          createAudioSprite,
          createGifSprite,
          createAiSprite: getGlobalCreateAiSprite(),
        });
      }
    }, [canRedo, redo, createVideoSprite, createImageSprite, createAudioSprite, createGifSprite]),

    ESCAPE: useCallback(() => {
      selectElements([]);
      hideContextMenu();
    }, [selectElements, hideContextMenu]),

    MOVE_UP: handleSpriteMove,
    MOVE_DOWN: handleSpriteMove,
    MOVE_LEFT: handleSpriteMove,
    MOVE_RIGHT: handleSpriteMove,
    MOVE_UP_FAST: handleSpriteMove,
    MOVE_DOWN_FAST: handleSpriteMove,
    MOVE_LEFT_FAST: handleSpriteMove,
    MOVE_RIGHT_FAST: handleSpriteMove,

    // Copy/Paste shortcuts
    COPY: useCallback(() => {
      if (selectedElement || selectedElements.length > 0) {
        copyElements();
      }
    }, [selectedElement, selectedElements, copyElements]),

    PASTE: useCallback(() => {
      pasteElements(addComponentToCanvas);
    }, [pasteElements, addComponentToCanvas]),
  };

  useKeyboardShortcuts(
    undefined,
    shortcutCallbacks,
    {
      enabled: true,
      targetRef: timelineContainerRef,
      preventDefault: true,
    }
  );

  const generateTicks = () => {
    if (duration <= 0) return [];

    const tickCount = Math.ceil(duration / TICK_INTERVAL) + 1;
    return Array.from({ length: tickCount }, (_, i) => {
      const timeValue = i * TICK_INTERVAL;
      const isWholeSec = timeValue % 1 === 0;

      return {
        id: i,
        timeValue,
        percentage: getTimePercentage(timeValue),
        isWholeSec,
        label: isWholeSec ? `${Math.round(timeValue)}s` : null
      };
    });
  };

  const renderTick = (tick) => (
    <div
      key={`tick-${tick.id}`}
      className="absolute"
      style={{ left: `${tick.percentage}%` }}
    >
      {!tick.isWholeSec && (
        <div
          className="w-0.5 bg-ag-black-400 h-2"
          style={{
            marginTop: '-1px'
          }}
        />
      )}

      {tick.label && (
        <div className="text-[10px] font-bold text-white absolute transform -translate-x-1/2 -translate-y-1/2">
          {tick.label}
        </div>
      )}
    </div>
  );

  React.useEffect(() => {
    if (isDraggingPlayhead) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingPlayhead, handleMouseMove, handleMouseUp]);

  // Handle caption phrase edit from CaptionSubTrack
  const handlePhraseEdit = useCallback((elementId, phraseIndex, newText) => {
    const targetElement = canvasElements.find(el => el.id === elementId);
    if (!targetElement?.componentData?.captions?.phrases) return;

    const newPhrases = [...targetElement.componentData.captions.phrases];
    newPhrases[phraseIndex] = {
      ...newPhrases[phraseIndex],
      text: newText,
    };

    updateElement(elementId, {
      componentData: {
        ...targetElement.componentData,
        captions: {
          ...targetElement.componentData.captions,
          phrases: newPhrases,
        },
      },
    });
  }, [canvasElements, updateElement]);

  // Handle caption phrase timing change from CaptionSubTrack drag handles
  const handlePhraseTimingChange = useCallback((elementId, phraseIndex, newStartMs, newEndMs) => {
    const targetElement = canvasElements.find(el => el.id === elementId);
    if (!targetElement?.componentData?.captions?.phrases) return;

    const newPhrases = [...targetElement.componentData.captions.phrases];
    const oldPhrase = newPhrases[phraseIndex];
    if (!oldPhrase) return;

    const oldStart = oldPhrase.start;
    const oldEnd = oldPhrase.end;
    const oldDuration = oldEnd - oldStart;
    const newDuration = newEndMs - newStartMs;

    // Build updated phrase with new timing
    const updatedPhrase = {
      ...oldPhrase,
      start: newStartMs,
      end: newEndMs,
    };

    // Proportionally adjust word-level timing if words exist
    if (oldPhrase.words && oldPhrase.words.length > 0 && oldDuration > 0) {
      updatedPhrase.words = oldPhrase.words.map(word => {
        // Calculate the word's relative position within the old phrase (0..1)
        const relativeStart = (word.start - oldStart) / oldDuration;
        const relativeEnd = (word.end - oldStart) / oldDuration;

        return {
          ...word,
          start: Math.round(newStartMs + relativeStart * newDuration),
          end: Math.round(newStartMs + relativeEnd * newDuration),
        };
      });
    }

    newPhrases[phraseIndex] = updatedPhrase;

    updateElement(elementId, {
      componentData: {
        ...targetElement.componentData,
        captions: {
          ...targetElement.componentData.captions,
          phrases: newPhrases,
        },
      },
    });
  }, [canvasElements, updateElement]);

  const renderLayerProperties = () => (
    <div
      style={{
        minHeight: 'max-content'
      }}
      onMouseDown={focusTimeline}
    >
      {sortedVisualElements.map((element) => {
        const isSelected = selectedElements.some(el => el.id === element.id);
        return (
          <div key={`layer-${element.id}`}>
            <div className='flex items-center select-none mb-2'>
              <div
                className={`h-12 py-1 text-xs truncate pr-2 cursor-pointer px-2 flex items-center ${isSelected
                  ? 'bg-ag-black-600 text-white border-l-2 border-ag-green'
                  : 'bg-ag-black-700 text-gray-300 hover:bg-ag-black-600'
                  }`}
                onClick={(e) => handleElementClick(element, e)}
                style={{ width: `${LAYER_NAME_WIDTH - 34}px` }}
              >
                {element.name || element.type || 'Element'}
              </div>
              <button
                type="button"
                className="h-12 w-[34px] flex items-center justify-center text-gray-300 hover:text-ag-black-400"
                onClick={() => toggleMute(element)}
                aria-label={`Toggle mute for ${element.name || element.type || 'element'}`}
              >
                {element.muted === true || element.muted === "true" ? (
                  <VolumeX size={16} />
                ) : (
                  <Volume size={16} />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTimelineTracks = () => {
    const { mode, elementId: draggedId, originalIndex, newVisualIndex, deltaY, isMultiDrag, multiDragData } = globalDragState || {};

    // During vertical drag, create a visual preview of final positions
    let visualElements = sortedVisualElements;
    let draggedElements = [];

    if (mode === 'vertical' && newVisualIndex !== undefined && originalIndex !== undefined) {
      // Get IDs of elements being dragged
      const draggedIds = isMultiDrag
        ? multiDragData?.map(d => d.id) || []
        : [draggedId];

      // Separate dragged and non-dragged elements
      draggedElements = sortedVisualElements.filter(el => draggedIds.includes(el.id));
      const nonDraggedElements = sortedVisualElements.filter(el => !draggedIds.includes(el.id));

      // Insert dragged elements at new position to preview final layout
      visualElements = [
        ...nonDraggedElements.slice(0, newVisualIndex),
        ...draggedElements,
        ...nonDraggedElements.slice(newVisualIndex)
      ];
    }


    return (
      <div style={{ width: `${timelineWidth}px`, minHeight: 'max-content' }} className="relative select-none pb-2">
        {/* Guideline for vertical drag */}
        {mode === 'vertical' && newVisualIndex !== undefined && (
          <div
            className="absolute left-0 right-0 h-0.5 bg-ag-green pointer-events-none z-50"
            style={{
              top: `${newVisualIndex * TRACK_HEIGHT}px`,
            }}
          />
        )}

        {visualElements.map((element, visualIndex) => {
          const isBeingDragged = isMultiDrag
            ? multiDragData?.some(d => d.id === element.id) && mode === 'vertical'
            : mode === 'vertical' && element.id === draggedId;

          let style = {};

          if (isBeingDragged) {
            // Calculate overlapping movement caused by reordering
            // We need to subtract the physical DOM movement from the visual transform
            // so the element stays exactly under the mouse
            let rowDiff = 0;
            if (newVisualIndex !== undefined && originalIndex !== undefined) {
              rowDiff = (newVisualIndex - originalIndex) * TRACK_HEIGHT;
            }

            // Elements being dragged follow the mouse
            style = {
              position: 'relative',
              zIndex: 100,
              // Adjust deltaY by the amount the element moved in the DOM
              transform: `translateY(${deltaY - rowDiff}px)`,
              transition: 'none',
              opacity: 0.5,
            };
          } else if (mode === 'vertical') {
            // Non-dragged elements smoothly animate to their preview positions
            style = {
              transition: 'transform 0.2s ease-out',
            };
          }

          return (
            <div key={element.id} style={style}>
              <TimelineTrack
                element={element}
                index={visualIndex}
                duration={duration}
                selectedElements={selectedElements}
                onElementClick={handleElementClick}
                globalDragState={globalDragState}
                setGlobalDragState={setGlobalDragState}
                reorderElement={reorderElement}
                TRACK_HEIGHT={TRACK_HEIGHT}
                timelineWidth={timelineWidth}
                snapEnabled={snapEnabled}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400">
      <p>No elements on canvas</p>
    </div>
  );

  return (
    <div
      ref={timelineContainerRef}
      className="select-none h-full flex flex-row rounded-lg bg-ag-black-700 text-white outline-none"
      tabIndex={0}
      onMouseDown={(e) => {
        handleTimelineClick(e);
        focusTimeline();
      }}
      onContextMenu={handleContextMenu}
    >
      <div className="flex flex-col h-full bg-ag-black-700 border-r border-ag-black-600 z-20">

        {/* Pinned Left Header Area */}
        <div className="flex flex-col flex-shrink-0">
          {/* Controls Row - strict h-8 matching Ruler */}
          <div className="h-8 flex items-center px-2" style={{ width: `${LAYER_NAME_WIDTH}px` }}>
            <TimelineControls
              duration={duration}
              onZoomChange={setCurrentZoom}
              snapEnabled={snapEnabled}
              onSnapToggle={handleSnapToggle}
            />
          </div>

          {/* Global Caption Label - strict h-8 matching CaptionSubTrack */}
          {sortedVisualElements.some(el => el.componentData?.captions?.phrases?.length > 0) && (
            <div
              className="h-8 flex items-center px-4 bg-ag-black-800/50 border-b border-ag-black-600 w-full"
              style={{ width: `${LAYER_NAME_WIDTH}px` }}
            >
              <span className="text-[10px] text-gray-400 font-medium">▸ Captions</span>
            </div>
          )}
        </div>

        {/* Explicit Spacer to sync left and right perfectly */}
        <div className="h-6 flex-shrink-0 bg-ag-black-700"></div>

        {/* Scrollable Left Body */}
        <div
          ref={layerPropertiesRef}
          className="flex-1 overflow-x-hidden overflow-y-hidden bg-ag-black-700 z-20"
          style={{ width: `${LAYER_NAME_WIDTH}px`, flexShrink: 0 }}
          onScroll={handleLayerPropertiesScroll}
        >
          {renderLayerProperties()}
        </div>
      </div>

      <div className="flex flex-1 flex-col h-full relative overflow-hidden">

        {/* Pinned Right Header Area */}
        <div className="flex flex-col flex-shrink-0 overflow-hidden" ref={timelineRef}>
          <div
            style={{
              width: `${timelineWidth}px`,
              transform: `translateX(-${scrollLeft}px)`
            }}
          >
            {/* Ruler Row - strict h-8 */}
            <div
              className="h-8 bg-ag-black-800 rounded-none border-b border-ag-black-600 relative cursor-pointer"
              style={{ width: `${timelineWidth}px` }}
              onClick={handleRulerClick}
              onMouseDown={(e) => {
                handlePlayheadMouseDown(e);
                focusTimeline();
              }}
            >
              <div className="absolute inset-0 flex items-center">
                {generateTicks().map(renderTick)}
              </div>
            </div>

            {/* Global Caption Track - strict h-8 */}
            {sortedVisualElements.some(el => el.componentData?.captions?.phrases?.length > 0) && (
              <CaptionSubTrack
                elements={sortedVisualElements}
                duration={duration}
                timelineWidth={timelineWidth}
                onPhraseEdit={handlePhraseEdit}
                onPhraseTimingChange={handlePhraseTimingChange}
              />
            )}
          </div>
        </div>

        {/* Explicit Spacer to sync left and right perfectly */}
        <div className="h-6 flex-shrink-0 bg-transparent"></div>

        {/* Scrollable Right Body */}
        {canvasElements.length === 0 ? (
          renderEmptyState()
        ) : (
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto overflow-hidden timeline-scroll"
            style={{
              overflowX: timelineWidth > (timelineRef.current?.clientWidth || 0) ? 'auto' : 'hidden'
            }}
            onScroll={handleScroll}
            onMouseDown={focusTimeline}
          >
            {renderTimelineTracks()}
          </div>
        )}
      </div>

      <div
        className="absolute pointer-events-none"
        style={{
          top: -17,
          left: `${LAYER_NAME_WIDTH}px`,
          right: 0,
          bottom: 0,
          overflow: 'visible',
          zIndex: 15
        }}
      >
        <div
          style={{
            width: `${timelineWidth}px`,
            height: '100%',
            position: 'relative',
            marginLeft: `-${scrollLeft}px`
          }}
        >
          <Playhead
            position={getTimePercentage(currentTime)}
            currentTime={currentTime}
            onMouseDown={handlePlayheadMouseDown}
            isDragging={isDraggingPlayhead}
          />
        </div>
      </div>

      {contextMenu.visible && (
        <div
          className="context-menu"
          ref={contextMenuRef}
          style={{
            position: "absolute",
            left: contextMenu.renderX,
            top: contextMenu.renderY,
            zIndex: 1000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ContextMenu
            targetElement={contextMenu.targetElement}
            onClose={hideContextMenu}
            onPaste={() => pasteElements(addComponentToCanvas)}
            context="timeline"
            onCustomAction={(action, element) => {
              if (action === 'replace_media' && onReplaceAction) {
                onReplaceAction(element);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Timeline;
